import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
  useNavigate,
} from "@tanstack/react-router";
import { lazy, Suspense, useEffect } from "react";

import { AuthCallback } from "./auth/AuthCallback";
import { ProtectedRoute } from "./auth/ProtectedRoute";
import { useCurrentUser } from "./auth/useCurrentUser";
import { PageLoader } from "./components/BrandedSpinner";
import { Layout } from "./components/Layout";
import { CommentOverlay } from "./components/review/CommentOverlay";
import { DEMO_MOCK } from "./lib/demo-flags";

// Demo MOCK pages are imported via React.lazy below — the dynamic
// `import()` keeps them in their own chunks that never load at runtime
// when DEMO_MOCK is off.

import { OnboardingCompleteStep } from "./routes/onboarding/complete";
import { OnboardingIdentityStep } from "./routes/onboarding/identity";
import { OnboardingSpifStep } from "./routes/onboarding/spif";
import { OnboardingTermsStep } from "./routes/onboarding/terms";
import { TraineeTrainingRoute } from "./routes/dashboard/partner/training/index";
import { TraineeTrainingPlayerRoute } from "./routes/dashboard/partner/training/$moduleId";
import { DashboardRedirector } from "./routes/dashboard/index";
import { ManagerInsightsRoute } from "./routes/dashboard/manager/insights";
import { ManagerDealsQueueRoute } from "./routes/dashboard/manager/deals";
import { ManagerDealsNewRoute } from "./routes/dashboard/manager/deals-new";
import { ManagerDealDecisionRoute } from "./routes/dashboard/manager/deals-detail";
import { ManagerSpifsQueueRoute } from "./routes/dashboard/manager/spifs";
import { AssetsHubRoute } from "./routes/dashboard/assets";
import { ManagerSpifDecisionRoute } from "./routes/dashboard/manager/spifs-detail";
import { PartnerDashboard } from "./routes/dashboard/partner";
import { PartnerInsightsRoute } from "./routes/dashboard/partner/insights/index";
import { PartnerDealsListRoute } from "./routes/dashboard/partner/deals";
import { PartnerTeamRoute } from "./routes/dashboard/partner/team";
import { PartnerDealsNewRoute } from "./routes/dashboard/partner/deals-new";
import { PartnerDealConfirmationRoute } from "./routes/dashboard/partner/deals-confirmation";
import { PartnerDealDetailRoute } from "./routes/dashboard/partner/deals-detail";
import { PartnerSpifsListRoute } from "./routes/dashboard/partner/spifs";
import { PartnerSpifsNewRoute } from "./routes/dashboard/partner/spifs-new";
import { PartnerSpifDetailRoute } from "./routes/dashboard/partner/spifs-detail";
import { ForbiddenRoute } from "./routes/forbidden";
import { ForgotPasswordRoute } from "./routes/forgot-password";
import { LoginRoute } from "./routes/login";
import { NotFoundRoute } from "./routes/not-found";
import { RequestAccessCodeStep } from "./routes/request-access/index";
import { RequestAccessDetailsStep } from "./routes/request-access/details";
import { RequestAccessSentStep } from "./routes/request-access/sent";
import { SetPasswordRoute } from "./routes/set-password";

// CHP-1166: the admin + settings surfaces sit behind role gates most sessions
// never pass, so their components are code-split out of the entry chunk —
// each `import()` becomes its own lazy chunk, fetched on first visit.
// `lazyNamed` adapts a named export to React.lazy's default-export contract
// (the pattern the demo pages below have always used).
function lazyNamed<M, K extends keyof M>(loader: () => Promise<M>, key: K) {
  return lazy(() => loader().then((m) => ({ default: m[key] as React.ComponentType })));
}

const suspenseFallback = <PageLoader className="container py-6" />;
const withSuspense = (Page: React.ComponentType) => () => (
  <Suspense fallback={suspenseFallback}>
    <Page />
  </Suspense>
);

const AdminDashboard = lazyNamed(() => import("./routes/dashboard/admin"), "AdminDashboard");
const AdminAccessRequestsRoute = lazyNamed(
  () => import("./routes/dashboard/admin/access-requests"),
  "AdminAccessRequestsRoute",
);
const AdminReferralCodesRoute = lazyNamed(
  () => import("./routes/dashboard/admin/referral-codes"),
  "AdminReferralCodesRoute",
);
const SpifManagementRoute = lazyNamed(
  () => import("./routes/dashboard/admin/spif-management"),
  "SpifManagementRoute",
);
const AdminTrainingListRoute = lazyNamed(
  () => import("./routes/dashboard/admin/training/index"),
  "AdminTrainingListRoute",
);
const AdminAuditTab = lazyNamed(() => import("./routes/dashboard/admin/audit"), "AdminAuditTab");
const AdminTrainingStatusTab = lazyNamed(
  () => import("./routes/dashboard/admin/training-status"),
  "AdminTrainingStatusTab",
);
const AdminPermissionsTab = lazyNamed(
  () => import("./routes/dashboard/admin/permissions"),
  "AdminPermissionsTab",
);
const AdminTrainingNewRoute = lazyNamed(
  () => import("./routes/dashboard/admin/training/new"),
  "AdminTrainingNewRoute",
);
const AdminTrainingEditorRoute = lazyNamed(
  () => import("./routes/dashboard/admin/training/$moduleId"),
  "AdminTrainingEditorRoute",
);
const AdminTrainingPreviewRoute = lazyNamed(
  () => import("./routes/dashboard/admin/training/$moduleId.preview"),
  "AdminTrainingPreviewRoute",
);
// The four user tabs share ./routes/dashboard/admin/users-table — Rollup
// resolves the repeated import() to one shared chunk, not four copies.
const AdminAdminsTab = lazyNamed(
  () => import("./routes/dashboard/admin/users-table"),
  "AdminAdminsTab",
);
const AdminPartnersTab = lazyNamed(
  () => import("./routes/dashboard/admin/users-table"),
  "AdminPartnersTab",
);
const AdminPendingTab = lazyNamed(
  () => import("./routes/dashboard/admin/users-table"),
  "AdminPendingTab",
);
const AdminTeamTab = lazyNamed(
  () => import("./routes/dashboard/admin/users-table"),
  "AdminTeamTab",
);
const SettingsRoute = lazyNamed(() => import("./routes/settings/index"), "SettingsRoute");
const ChangelogRoute = lazyNamed(() => import("./routes/changelog"), "ChangelogRoute");
// CHP-1220: the CM Admin Hub must stay dynamic-only — a static import of
// ./routes/dashboard/manager/admin (which statically imports dashboard/admin +
// admin/users-table) pins all three back into the entry chunk and silently
// undoes the CHP-1166 split (Rollup only warns; nothing in CI fails).
const CmAdminHub = lazyNamed(() => import("./routes/dashboard/manager/admin"), "CmAdminHub");
const CmAdminPartnersTab = lazyNamed(
  () => import("./routes/dashboard/manager/admin"),
  "CmAdminPartnersTab",
);

const rootRoute = createRootRoute({
  component: () => (
    <>
      <Layout>
        <Outlet />
      </Layout>
      {/* comment-overlay build: in-app review FAB + panel. Self-gates on the
          backend `comments_enabled` flag (from /auth/me) and a signed-in user;
          renders null otherwise. Lives inside the root route component so router
          hooks (useRouterState, router.navigate) resolve. */}
      <CommentOverlay />
    </>
  ),
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  beforeLoad: () => {
    throw redirect({ to: "/dashboard" });
  },
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: LoginRoute,
});

// ADR-0010: PRM-rendered forgot-password and set-password (the latter
// is the landing for the invitation/recovery email link).
const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/forgot-password",
  component: ForgotPasswordRoute,
});

const setPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/set-password",
  component: SetPasswordRoute,
});

const callbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/auth/callback",
  component: AuthCallback,
});

const forbiddenRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/forbidden",
  component: ForbiddenRoute,
});

// /dashboard is a role-aware redirector. B-19 replaced the previous
// `require()` call (broken in ESM) with a static import at the top of this
// module.
const dashboardIndexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dashboard",
  component: DashboardRedirector,
});

const partnerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dashboard/partner",
  component: () => (
    <ProtectedRoute roles={["channel_partner", "admin"]}>
      <PartnerDashboard />
    </ProtectedRoute>
  ),
});

// CHP-520: the shared CM/RM/RA Operations Core Main Dashboard (managerRoute +
// its Overview/Team/Partners/Training tabs) was removed. Staff now land on the
// Deals queue (CM/RA) or Program Insights (RM) — see DashboardRedirector — and
// the CM team-training report moved to the CM Admin Hub's Training Status tab.
// (Supersedes the review-r2 partial removal that kept the shell + an Overview
// redirect; CHP-520 drops the whole surface.)
// Program Insights (Wave 2): the CM/RM/RA "Channel Partner Program Insights"
// surface. A TOP-LEVEL route (NOT a managerRoute child) so it renders its own
// full page, not inside the CM dashboard's title/tab shell — same pattern as
// cmAdminHubRoute below. Program-wide for every staff role (Wave-2 decision #1),
// so it gates on the manager-OR-admin role set, not RM-only.
const managerInsightsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dashboard/manager/insights",
  component: () => (
    <ProtectedRoute roles={["channel_manager", "admin"]}>
      <ManagerInsightsRoute />
    </ProtectedRoute>
  ),
});

// CHP-288: the CM Admin Hub is a TOP-LEVEL route, not a managerRoute child —
// it must NOT render inside the CM dashboard's header/tab shell (same pattern
// as adminReferralCodesRoute et al. under /dashboard/admin/*). CM-only: RM/RA
// use /dashboard/admin instead.
const cmAdminHubRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dashboard/manager/admin",
  component: () => (
    <ProtectedRoute roles={["channel_manager"]}>
      <Suspense fallback={suspenseFallback}>
        <CmAdminHub />
      </Suspense>
    </ProtectedRoute>
  ),
});
// CHP-852: the CM Admin Hub's "Training Status" tab + child route were removed
// (designer review). The hub is partner-only now; only the index partners route
// remains as a child of cmAdminHubRoute.
const cmAdminHubPartnersRoute = createRoute({
  getParentRoute: () => cmAdminHubRoute,
  path: "/",
  component: withSuspense(CmAdminPartnersTab),
});

const adminRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dashboard/admin",
  component: () => (
    // CHP-446 C1: the Admin Hub + its tabs (children of this route, rendered via
    // AdminDashboard's Outlet) are RM-only. The nav link was already RM-only
    // (RACCPE-17); requireMaster closes the by-URL hole that still admitted a
    // constrained RA. access-requests / referral-codes / spif-management /
    // training authoring are separate top-level routes and stay RA-reachable.
    <ProtectedRoute roles={["admin"]} requireMaster>
      <Suspense fallback={suspenseFallback}>
        <AdminDashboard />
      </Suspense>
    </ProtectedRoute>
  ),
});
const adminPartnersTabRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/",
  component: withSuspense(AdminPartnersTab),
});
const adminTeamTabRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/team",
  component: withSuspense(AdminTeamTab),
});
const adminAdminsTabRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/admins",
  component: withSuspense(AdminAdminsTab),
});
// Slice 4c: pending users have role=NULL + status='pending' so they
// don't appear under the three role-pinned tabs above. This leaf
// drops the role filter and locks status to pending.
const adminPendingTabRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/pending",
  component: withSuspense(AdminPendingTab),
});
// Deals-gap W2: full-partner-pool training certification report. A tab child so
// it renders inside the Admin Hub chrome like the user tabs. CHP-809 (#17):
// reachable by RM ONLY — the parent adminRoute is gated `requireMaster`
// (CHP-446 C1), so an RA reaches the partner pool via the CSV export, not here.
const adminTrainingStatusTabRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/training-status",
  component: withSuspense(AdminTrainingStatusTab),
});
// CHP-335 (UAT ST-D-04): read-only audit-trail tab over GET /admin/audit.
// RM-only like every Admin Hub child (parent adminRoute gates requireMaster).
const adminAuditTabRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/audit",
  component: withSuspense(AdminAuditTab),
});
// CHP-447: standalone Permissions matrix tab (CM/RA per-user toggles).
const adminPermissionsTabRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/permissions",
  component: withSuspense(AdminPermissionsTab),
});

// M3: legacy /profile lives on as a permanent redirect to the new
// Settings home so any stale bookmark or back-button hit lands somewhere
// useful instead of a 404.
const profileRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/profile",
  beforeLoad: () => {
    throw redirect({ to: "/settings" });
  },
});

// Settings — single scrolling page (spec 701104186: "Tabular format → Have
// all on one page"). The parent is a bare ProtectedRoute + <Outlet /> shell
// (no tab sidebar); the index renders the consolidated page with Profile,
// Security, and Notifications stacked. The former per-tab paths
// (/settings/profile|notifications|security) survive as permanent redirects
// to /settings so old bookmarks and deep links still resolve.
const settingsLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings",
  component: () => (
    <ProtectedRoute>
      <Outlet />
    </ProtectedRoute>
  ),
});
const settingsIndexRoute = createRoute({
  getParentRoute: () => settingsLayoutRoute,
  path: "/",
  component: withSuspense(SettingsRoute),
});
const settingsProfileRoute = createRoute({
  getParentRoute: () => settingsLayoutRoute,
  path: "/profile",
  beforeLoad: () => {
    throw redirect({ to: "/settings" });
  },
});
const settingsNotificationsRoute = createRoute({
  getParentRoute: () => settingsLayoutRoute,
  path: "/notifications",
  beforeLoad: () => {
    throw redirect({ to: "/settings" });
  },
});
// CHP-809 findings 7 & 8: the orphan /settings/payouts (SPIF coming-soon —
// spec directive "remove the SPIFs section entirely") and /settings/team
// (outside the Settings Flow scope; duplicates the shipped
// /dashboard/partner/team surface, with an inverted RBAC gate) routes were
// removed. Neither was linked from anywhere in the SPA.
const settingsSecurityRoute = createRoute({
  getParentRoute: () => settingsLayoutRoute,
  path: "/security",
  beforeLoad: () => {
    throw redirect({ to: "/settings" });
  },
});

// M2: public access-request flow
const requestAccessRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/request-access",
  component: RequestAccessCodeStep,
});
const requestAccessDetailsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/request-access/details",
  component: RequestAccessDetailsStep,
});
const requestAccessSentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/request-access/sent",
  component: RequestAccessSentStep,
});

// Account Creation slice 1: post-invite onboarding wizard. Three sibling
// routes; ProtectedRoute redirects users with tc_accepted_at == null to
// /onboarding/identity (the first step). Each step does its own auth
// check via useCurrentUser so the wizard sits OUTSIDE ProtectedRoute
// (the user is signed in but pre-T&C — ProtectedRoute would loop us
// back here).
const onboardingIndexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/onboarding",
  beforeLoad: () => {
    throw redirect({ to: "/onboarding/identity" });
  },
});
const onboardingIdentityRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/onboarding/identity",
  component: OnboardingIdentityStep,
});
const onboardingSpifRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/onboarding/spif",
  component: OnboardingSpifStep,
});
const onboardingTermsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/onboarding/terms",
  component: OnboardingTermsStep,
});
// Flow A step 6 — Registration Complete / Welcome (CHP-207). Reached after
// a PO accepts the T&Cs; sits outside ProtectedRoute like the other steps.
const onboardingCompleteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/onboarding/complete",
  component: OnboardingCompleteStep,
});
// Legacy /accept-terms — kept as a permanent redirect to /onboarding/terms
// so older invitation email links still resolve.
const acceptTermsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/accept-terms",
  beforeLoad: () => {
    throw redirect({ to: "/onboarding/terms" });
  },
});

// M2: admin queue + referral-codes. Both gated to admins.
const adminAccessRequestsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dashboard/admin/access-requests",
  component: () => (
    <ProtectedRoute roles={["admin"]}>
      <Suspense fallback={suspenseFallback}>
        <AdminAccessRequestsRoute />
      </Suspense>
    </ProtectedRoute>
  ),
});
const adminReferralCodesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dashboard/admin/referral-codes",
  component: () => (
    <ProtectedRoute roles={["admin"]}>
      <Suspense fallback={suspenseFallback}>
        <AdminReferralCodesRoute />
      </Suspense>
    </ProtectedRoute>
  ),
});
const adminSpifManagementRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dashboard/admin/spif-management",
  component: () => (
    <ProtectedRoute roles={["admin"]}>
      <Suspense fallback={suspenseFallback}>
        <SpifManagementRoute />
      </Suspense>
    </ProtectedRoute>
  ),
});

// RACCPE-3: training authoring (RM + RA). /new is a literal and must register
// before /$moduleId so it isn't captured as a module id.
const adminTrainingListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dashboard/admin/training",
  component: () => (
    <ProtectedRoute roles={["admin"]}>
      <Suspense fallback={suspenseFallback}>
        <AdminTrainingListRoute />
      </Suspense>
    </ProtectedRoute>
  ),
});
const adminTrainingNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dashboard/admin/training/new",
  component: () => (
    <ProtectedRoute roles={["admin"]}>
      <Suspense fallback={suspenseFallback}>
        <AdminTrainingNewRoute />
      </Suspense>
    </ProtectedRoute>
  ),
});
const adminTrainingEditorRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dashboard/admin/training/$moduleId",
  component: () => (
    <ProtectedRoute roles={["admin"]}>
      <Suspense fallback={suspenseFallback}>
        <AdminTrainingEditorRoute />
      </Suspense>
    </ProtectedRoute>
  ),
});
// CHP-421: preview a module as a trainee sees it. Deeper path than the editor
// ($moduleId/preview), so it never collides with the $moduleId route.
const adminTrainingPreviewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dashboard/admin/training/$moduleId/preview",
  component: () => (
    <ProtectedRoute roles={["admin"]}>
      <Suspense fallback={suspenseFallback}>
        <AdminTrainingPreviewRoute />
      </Suspense>
    </ProtectedRoute>
  ),
});

// Epic 2: deals. Sibling routes to partner/manager dashboards (no shared
// chrome — they're standalone pages with their own back-link breadcrumb).
// IMPORTANT: /deals/new and /deals/pending must register BEFORE /deals/$dealId
// so the literal segments win the routing match against the UUID param.
// Slice 3: PO bulk-invites BDMs. Gated to channel_partner (the
// endpoint also rejects partners without an org_id; the dashboard
// only shows the entry point when a user has one).
const partnerTeamRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dashboard/partner/team",
  // CHP-808: PO-only per Flow A/B. POST /partners/me/team-invites hard-rejects
  // any non-channel_partner with 403, so `admin` is dropped from the route gate
  // (admins invite Channel Partners via the Admin Hub, not this surface). The
  // component additionally guards on is_org_admin so a BDM deep-link sees the
  // explanation, not a form they can't submit.
  component: () => (
    <ProtectedRoute roles={["channel_partner"]}>
      <PartnerTeamRoute />
    </ProtectedRoute>
  ),
});

// po-dashboard: the PO "Insights & Admin" surface. Gated to channel_partner;
// the component adds an is_org_admin guard so a BDM deep-link sees the same
// "Program Owner only" explanation as /dashboard/partner/team (the backend
// /deals/team-* endpoints also 403 any non-PO).
const partnerInsightsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dashboard/partner/insights",
  component: () => (
    <ProtectedRoute roles={["channel_partner"]}>
      <PartnerInsightsRoute />
    </ProtectedRoute>
  ),
});

const partnerDealsListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dashboard/partner/deals",
  component: () => (
    <ProtectedRoute roles={["channel_partner", "admin"]}>
      <PartnerDealsListRoute />
    </ProtectedRoute>
  ),
});
const partnerDealsNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dashboard/partner/deals/new",
  component: () => (
    <ProtectedRoute roles={["channel_partner", "admin"]}>
      <PartnerDealsNewRoute />
    </ProtectedRoute>
  ),
});
// Confirmation registers BEFORE the $dealId catch-all so the literal
// "confirmation" segment wins the route match (same pattern as /deals/new).
const partnerDealConfirmationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dashboard/partner/deals/$dealId/confirmation",
  component: () => (
    <ProtectedRoute roles={["channel_partner", "admin"]}>
      <PartnerDealConfirmationRoute />
    </ProtectedRoute>
  ),
});
const partnerDealDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dashboard/partner/deals/$dealId",
  component: () => (
    <ProtectedRoute roles={["channel_partner", "admin"]}>
      <PartnerDealDetailRoute />
    </ProtectedRoute>
  ),
});
const managerDealsQueueRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dashboard/manager/deals",
  component: () => (
    <ProtectedRoute roles={["channel_manager", "admin"]}>
      <ManagerDealsQueueRoute />
    </ProtectedRoute>
  ),
});
// CHP-202 register-on-behalf. The literal "new" registers BEFORE $dealId so
// it wins the route match against the UUID param (same pattern as the partner
// /deals/new route).
const managerDealsNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dashboard/manager/deals/new",
  component: () => (
    <ProtectedRoute roles={["channel_manager", "admin"]}>
      <ManagerDealsNewRoute />
    </ProtectedRoute>
  ),
});
const managerDealDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dashboard/manager/deals/$dealId",
  component: () => (
    <ProtectedRoute roles={["channel_manager", "admin"]}>
      <ManagerDealDecisionRoute />
    </ProtectedRoute>
  ),
});

// Asset library (the Assets Hub) — one top-level route for every active role
// (BDM/PO/CM/RM/RA). Admin-only management surfaces render conditionally
// inside the page, gated again on the backend (RM/RA). Replaces the Epic 3
// partner Library + manager Documents pages.
const assetsHubRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dashboard/assets",
  component: () => (
    <ProtectedRoute roles={["channel_partner", "channel_manager", "admin"]}>
      <AssetsHubRoute />
    </ProtectedRoute>
  ),
});

// RACCPE-6 / Slice 4: trainee Training hub + module player. Replaces the demo
// mockup at the same path. Hub before player (different depth; no overlap).
// Trainees are channel_partner (BDM/PO); admin may preview. CHP-1013: a
// channel_manager gets the same surface VIEW-ONLY (the components suppress all
// progress/quiz affordances by role) — the backend reads were always CM-open.
const partnerTrainingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dashboard/partner/training",
  component: () => (
    <ProtectedRoute roles={["channel_partner", "channel_manager", "admin"]}>
      <TraineeTrainingRoute />
    </ProtectedRoute>
  ),
});
const partnerTrainingPlayerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dashboard/partner/training/$moduleId",
  component: () => (
    <ProtectedRoute roles={["channel_partner", "channel_manager", "admin"]}>
      <TraineeTrainingPlayerRoute />
    </ProtectedRoute>
  ),
});

// Epic 4: SPIF claims. `/new` before `/$claimId` (B-12 / static-wins).
const partnerSpifsListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dashboard/partner/spifs",
  component: () => (
    <ProtectedRoute roles={["channel_partner", "admin"]}>
      <PartnerSpifsListRoute />
    </ProtectedRoute>
  ),
});
const partnerSpifsNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dashboard/partner/spifs/new",
  component: () => (
    <ProtectedRoute roles={["channel_partner", "admin"]}>
      <PartnerSpifsNewRoute />
    </ProtectedRoute>
  ),
});
const partnerSpifDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dashboard/partner/spifs/$claimId",
  component: () => (
    <ProtectedRoute roles={["channel_partner", "admin"]}>
      <PartnerSpifDetailRoute />
    </ProtectedRoute>
  ),
});
const managerSpifsQueueRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dashboard/manager/spifs",
  component: () => (
    <ProtectedRoute roles={["channel_manager", "admin"]}>
      <ManagerSpifsQueueRoute />
    </ProtectedRoute>
  ),
});
const managerSpifDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dashboard/manager/spifs/$claimId",
  component: () => (
    <ProtectedRoute roles={["channel_manager", "admin"]}>
      <ManagerSpifDecisionRoute />
    </ProtectedRoute>
  ),
});

// Demo-only mocked partner routes. All RBAC-gated to partner+admin so
// `<ProtectedRoute>` enforces the same role boundary as a real backend
// would. When DEMO_MOCK is off, the array is empty AND the React.lazy /
// import() calls below are inside the dead branch — Rollup eliminates
// them, the demo modules never appear in the production bundle.
const demoPartnerRoles = ["channel_partner", "admin"] as const;

const demoPartnerRoutes = (() => {
  if (!DEMO_MOCK) return [];

  // All demo page imports live INSIDE this dead-when-flag-off branch so
  // the Rollup tree-shaker drops both the import() calls and their
  // chunks from a flag-off production build. Verified by build + grep —
  // see the rollback section of docs/runbooks/cloudflare-demo.md.
  //
  // Epic 2 replaced the demo /deals and /register-deal entries with real
  // routes; Epic 3 replaces the demo /library entry; Epic 4 replaces the
  // demo /spifs entry. The mock data files apps/web/src/demo/mocks/
  // {deals,library,spifs}.ts stay in tree as reference.
  // /dashboard/partner/training is now a real route (RACCPE-6) — registered
  // outside this demo block.
  const DemoLeaderboard = lazyNamed(() => import("./demo/pages/Leaderboard"), "Leaderboard");
  const DemoAnnouncements = lazyNamed(() => import("./demo/pages/Announcements"), "Announcements");
  const DemoAskExpert = lazyNamed(() => import("./demo/pages/AskExpert"), "AskExpert");

  const wrap = (Page: React.ComponentType) => () => (
    <ProtectedRoute roles={[...demoPartnerRoles]}>
      <Suspense fallback={suspenseFallback}>
        <Page />
      </Suspense>
    </ProtectedRoute>
  );

  return [
    createRoute({
      getParentRoute: () => rootRoute,
      path: "/dashboard/partner/leaderboard",
      component: wrap(DemoLeaderboard),
    }),
    createRoute({
      getParentRoute: () => rootRoute,
      path: "/dashboard/partner/announcements",
      component: wrap(DemoAnnouncements),
    }),
    createRoute({
      getParentRoute: () => rootRoute,
      path: "/dashboard/partner/ask",
      component: wrap(DemoAskExpert),
    }),
  ];
})();

// Design-system reference (CHP-522). Registered in every build (lazy-loaded, so
// it costs prod nothing until visited) and gated at render time on the SAME
// runtime `comments_enabled` flag as the review comment-overlay (from /auth/me).
// Effect: it's reachable WITH the review FAB exactly where review is enabled —
// DEV (always, public, for quick light/dark viewing) and any built env with the
// flag on (e.g. staging) — and is inert in real prod (flag off → redirect). So
// the designer reviews the design system with the overlay just like any screen.
const Styleguide = lazy(() =>
  import("./demo/pages/Styleguide").then((m) => ({ default: m.Styleguide })),
);

function StyleguideRoute() {
  const { data: me, isLoading, isAuthenticated, isAuthLoading } = useCurrentUser();
  const navigate = useNavigate();
  const allowed = import.meta.env.DEV || me?.comments_enabled === true;
  // On a built image, wait for the auth/me round-trip before deciding — don't
  // bounce a signed-in reviewer mid-load.
  const resolving = !import.meta.env.DEV && (isAuthLoading || (isAuthenticated && isLoading));

  useEffect(() => {
    if (!allowed && !resolving) {
      navigate({ to: "/dashboard" });
    }
  }, [allowed, resolving, navigate]);

  if (!allowed) return <PageLoader className="p-8" />;
  return (
    <Suspense fallback={<PageLoader className="p-8" />}>
      <Styleguide />
    </Suspense>
  );
}

const styleguideRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/styleguide",
  component: StyleguideRoute,
});

const changelogRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/changelog",
  component: () => (
    <ProtectedRoute>
      <Suspense fallback={suspenseFallback}>
        <ChangelogRoute />
      </Suspense>
    </ProtectedRoute>
  ),
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  forgotPasswordRoute,
  setPasswordRoute,
  callbackRoute,
  forbiddenRoute,
  dashboardIndexRoute,
  partnerRoute,
  managerInsightsRoute,
  cmAdminHubRoute.addChildren([cmAdminHubPartnersRoute]),
  adminRoute.addChildren([
    adminPartnersTabRoute,
    adminTeamTabRoute,
    adminAdminsTabRoute,
    adminPermissionsTabRoute,
    adminPendingTabRoute,
    adminTrainingStatusTabRoute,
    adminAuditTabRoute,
  ]),
  profileRoute,
  settingsLayoutRoute.addChildren([
    settingsIndexRoute,
    settingsProfileRoute,
    settingsNotificationsRoute,
    settingsSecurityRoute,
  ]),
  changelogRoute,
  requestAccessRoute,
  requestAccessDetailsRoute,
  requestAccessSentRoute,
  onboardingIndexRoute,
  onboardingIdentityRoute,
  onboardingSpifRoute,
  onboardingTermsRoute,
  onboardingCompleteRoute,
  acceptTermsRoute,
  adminAccessRequestsRoute,
  adminReferralCodesRoute,
  adminSpifManagementRoute,
  adminTrainingListRoute,
  adminTrainingNewRoute,
  adminTrainingPreviewRoute,
  adminTrainingEditorRoute,
  partnerTrainingRoute,
  partnerTrainingPlayerRoute,
  partnerTeamRoute,
  partnerInsightsRoute,
  partnerDealsListRoute,
  partnerDealsNewRoute,
  partnerDealConfirmationRoute,
  partnerDealDetailRoute,
  managerDealsQueueRoute,
  managerDealsNewRoute,
  managerDealDetailRoute,
  assetsHubRoute,
  partnerSpifsListRoute,
  partnerSpifsNewRoute,
  partnerSpifDetailRoute,
  managerSpifsQueueRoute,
  managerSpifDetailRoute,
  ...demoPartnerRoutes,
  styleguideRoute,
]);

export const router = createRouter({
  routeTree,
  // Hit when no other route matches. Without this, TanStack Router
  // falls back to a stock `<p>Not Found</p>` and emits a dev warning.
  defaultNotFoundComponent: NotFoundRoute,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
